import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isArray, isEmpty, isUndefined, sortBy } from 'lodash';
import useDataSheetDialogActions from 'common/hooks/useDataSheetDialogActions';
import { Format } from 'common/types/format';
import { ConfirmationDelete, ConfirmationDialog } from 'components/Dialogs';
import { CellReferenceBar } from 'components/FeaturedSpreadsheet/components';
import RowIndicator from 'components/FeaturedSpreadsheet/components/RowIndicator';
import { DEFAULT_CURRENCY, DEFAULT_CURRENCY_SYMBOL } from 'components/FeaturedSpreadsheet/constants';
import CurrencyChipContext from 'components/FeaturedSpreadsheet/context/CurrencyChipContext';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { buildStripeRowParityMap } from 'components/FeaturedSpreadsheet/utilities/buildStripeRowParityMap';
import { StripeRowConfig } from 'components/FeaturedSpreadsheet/utilities/isStripeExemptRow';
import { GridSkeleton } from 'components/Grid';
import ScalarDatasheetProps from 'components/ScalarDatasheet/ScalarDatasheet.props';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { ConfirmUnsavedChanges } from 'components/Spreadsheet/components';
import { DialogContext } from 'context';
import { useLayoutContext } from 'context/LayoutContext';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { EvaluatedCell, ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { getNumberValue, getValueByAccessorString } from 'utilities';
import getCancelDialogOptions from 'utilities/getCancelDialogOptions';
import { ContentColumns, SelectionSummaryBar, TitlesColumn, TotalsColumn } from './components';
import { COLUMN_INDICATOR_HEIGHT } from './components/content-column/ContentColumns.util';
import DragReorderContext from './components/DragReorderContext';
import {
  applyColumnReorderToGrid,
  applyResizeToGrid,
  columnTypeToLabel,
  extractCellLabel,
} from './utilities/gridColumnTransformers';

const PREFIX = 'ScalarSpreadsheet';

const classes = {
  relative: `${PREFIX}-relative`,
  bottomBar: `${PREFIX}-bottomBar`,
  cellReferenceWrapper: `${PREFIX}-cellReferenceWrapper`,
};

const ROW_INDICATOR_INSET = 11;
const BOTTOM_BAR_TOP_PADDING = 3;

const Root = styled('div')({
  [`& .${classes.relative}`]: {
    position: 'relative',
  },
  [`& .${classes.bottomBar}`]: {
    display: 'flex',
    alignItems: 'stretch',
    overflow: 'hidden',
    paddingLeft: ROW_INDICATOR_INSET,
    paddingTop: BOTTOM_BAR_TOP_PADDING,
  },
  [`& .${classes.cellReferenceWrapper}`]: {
    flex: 1,
    minWidth: 0,
  },
});

const GET_SIDE_COLUMNS_DELAY = 100;
// Define interfaces for the component props
interface TableTerms {
  tableName?: string;
  tableSlug?: string;
  columnName?: string;
  pluralColumnName?: string;
}

interface Column {
  id?: string | number;
  key?: string;
  label?: string;
  order?: number;
  isLTM?: boolean;
  isNTM?: boolean;
  isAdditionalHistoricalYear?: boolean;
  showAdditionalHistoricalYear?: boolean;
  isChildWithDivider?: boolean;
  isParentWithDivider?: boolean;
  needsSpacer?: boolean;
  isParent?: boolean;
  quarter?: number;
  parentColumn?: string | number;
  // TODO: Add more properties as needed
}

interface CellChange {
  cell: EvaluatedCell;
  value: EvaluatedCell['value'];
}

export interface ScalarSpreadsheetXlsxPreviewOptions {
  allowRowLegendSelection?: boolean;
  allowMergedSelectionIndicatorSpan?: boolean;
  defaultColumnWidths?: { column: string; width: number }[];
  defaultRowHeights?: { height: number; rowNumber: number }[];
  disableLegendSelection?: boolean;
  enableCopyFeedback?: boolean;
  frozenPaneColumnCellKey?: string;
  frozenPaneRowCellKey?: string;
  onToggleFrozenColumnsAtCell?: (cellKey: string) => void;
  onToggleFrozenRowsAtCell?: (cellKey: string) => void;
  onRowResize?: (rowNumber: number, height: number) => void;
  reserveColumnIndicatorSpace?: boolean;
  resizeHandlesInColumnLegend?: boolean;
  rowHeights?: { height: number; rowNumber: number }[];
}

interface ActiveCell {
  cellInfo?: {
    sheet?: {
      name?: string;
    };
  };
  sheet?: {
    name?: string;
  };
  // TODO: Add more properties as needed
}

interface Sheet {
  name: string;
  alphabet?: string[];
  columns: Column[];
  data?: unknown[][];
  titleData?: unknown[][];
  totalData?: unknown[][];
  cells?: Record<string, { style?: ICell['style'] }>;
  // TODO: Add more properties as needed
}

export interface ScalarSpreadsheetProps {
  addMultipleColumns?: (arg: any) => void;
  addMultipleRows?: (arg: any) => void;
  addSingleRow?: (arg: any) => void;
  allowAddMultipleColumns?: boolean;
  allowAddMultipleRows?: boolean;
  allowAddSingleRow?: boolean;
  allowChangeDataType?: boolean;
  allowCloneColumn?: boolean;
  allowConfirmAndDeleteColumn?: boolean;
  allowCopyColumn?: boolean;
  allowCopyRows?: boolean;
  allowDeleteColumn?: boolean;
  allowDeleteOnlySpecificColumn?: boolean;
  allowDeleteRow?: boolean;
  allowReorderColumns?: boolean;
  allowSortColumn?: boolean;
  alwaysDisplayLegend?: boolean;
  cells?: Record<string, any>;
  changeRowDataTypeFn?: (arg: any) => void;
  children?: React.ReactNode;
  className?: string;
  cloneColumn?: (arg: any) => void;
  collapsibleColumns?: Record<string, any>;
  columns?: Column[];
  colTitleRow?: number;
  customDeleteConfirmation?: boolean;
  data: any[];
  deleteColumn?: (columnIndex: number, columnId: string | number, wb: any, cb: () => void) => void;
  // Batch-delete handler for multi-row selections; passed through to FeaturedSpreadsheetContext.
  // Only tables that support multi-row delete (e.g., GPC) provide this prop.
  deleteMultipleRowsFn?: (items: any[]) => void;
  deleteRowFn?: (arg: any) => void;
  disabled?: boolean;
  disableDeleteBtnOnFirstColumn?: boolean;
  disableVerticalScroll?: boolean;
  disablePerfectScrollbar?: boolean;
  displayColumnIndicator?: boolean;
  displayDeleteRowBtn?: boolean;
  displayRowIndicator?: boolean;
  editorForTitles?: React.ComponentType<any>;
  fieldAttributes?: Record<string, any>;
  format?: Format;
  formatDispatch?: (arg: any) => void;
  hasColTitle?: boolean;
  id?: string;
  ignoreCurrencyChangeNumber?: number;
  initialCurrencyType?: string;
  isLedgerTable?: boolean;
  isReadOnly?: boolean;
  isToolbarVisible?: boolean;
  linkCurrencyChips?: boolean;
  matchCurrencyPadding?: boolean;
  notShowingQuarters?: boolean;
  onChange: (cell: EvaluatedCell, value: any) => void;
  onTitleCellsChanges?: (arg: any) => void;
  page?: string;
  referencedValues?: Record<string, any>;
  reverseParser?: (...args: never[]) => unknown;
  reverseParserOutputAccessor?: string;
  rowConfig?: any[];
  rowGroups?: Record<string, any>;
  selectionSummaryUsesCellCurrencyFormat?: boolean;
  setCollapsibleColumns?: (arg: any) => void;
  setColumns?: (arg: any) => void;
  setIsDisplayingRowNumbers?: (arg: boolean) => void;
  setIsDisplayingToolbar?: (arg: boolean) => void;
  setRowGroups?: (arg: any) => void;
  setSpreadsheetWidth?: (arg: number) => void;
  setTitleCells?: (arg: any) => void;
  sheet: Sheet;
  showPreviousColsDivider?: boolean;
  showTitlesColumn?: boolean;
  showToolbar?: boolean;
  showTotalColumn?: boolean;
  sortColumnFn?: (columnKey: string, fieldIsString: boolean, fieldIsDate: boolean) => void;
  sortedColumn?: Record<string, any>;
  tableData?: Record<string, any>;
  tableTerms?: TableTerms;
  titleData?: any[];
  toggleRows?: () => void;
  totalData?: any[];
  totalsCells?: Record<string, any>;
  updateCells?: (arg: any) => void;
  updateTotalsCells?: (arg: any) => void;
  validatedTitleCells?: Record<string, any>;
  virtualization?: ScalarDatasheetProps['virtualization'];
  workbook?: Record<string, any>;
  disableUnsavedChanges?: boolean;
  columnWidths?: { column: string; width: number }[];
  onColumnResize?: (column: string, width: number) => void;
  columnTypes?: string[];
  onColumnReorder?: (sourceColumn: string, targetColumn: string) => void;
  onAddColumn?: () => void;
  xlsxPreviewOptions?: ScalarSpreadsheetXlsxPreviewOptions;
}

const ADD_COLUMN_LABEL_THRESHOLD = 15;

const getLegendCellLayoutStyle = (style?: React.CSSProperties | null, columnWidth?: number): React.CSSProperties => ({
  ...(style?.display ? { display: style.display } : {}),
  ...(columnWidth != null
    ? { width: columnWidth, minWidth: columnWidth, maxWidth: columnWidth }
    : {
      ...(style?.width ? { width: style.width } : {}),
      ...(style?.minWidth ? { minWidth: style.minWidth } : {}),
      ...(style?.maxWidth ? { maxWidth: style.maxWidth } : {}),
    }),
});

const resolveXlsxPreviewChangeCell = (cell: EvaluatedCell): EvaluatedCell => {
  if (typeof cell.onChange === 'function') {
    return cell;
  }

  if (!cell.key) {
    return cell;
  }

  const sheetCells = cell.sheet?.cells as Record<string, ICell> | undefined;
  return (sheetCells?.[cell.key] as EvaluatedCell | undefined) ?? cell;
};

const syncXlsxPreviewProxyCell = (
  proxyCell: EvaluatedCell,
  changeCell: EvaluatedCell,
  fallbackValue: EvaluatedCell['value']
): void => {
  if (proxyCell === changeCell) {
    return;
  }

  // XLSX preview cells can proxy real workbook cells; mirror the real edit result so the drawer UI stays in sync.
  Object.assign(proxyCell, {
    expr: changeCell.expr,
    isValid: changeCell.isValid,
    value: changeCell.value ?? fallbackValue,
  });
  proxyCell.dispatchEvent?.();
};

const ScalarSpreadsheet: React.FC<ScalarSpreadsheetProps> = ({
  addMultipleColumns,
  addMultipleRows,
  addSingleRow,
  allowAddMultipleColumns,
  allowAddMultipleRows = false,
  allowAddSingleRow = false,
  allowChangeDataType = false,
  allowCloneColumn,
  allowConfirmAndDeleteColumn,
  allowCopyColumn = false,
  allowCopyRows = false,
  allowDeleteColumn = false,
  allowDeleteOnlySpecificColumn,
  allowDeleteRow,
  allowReorderColumns,
  allowSortColumn = false,
  alwaysDisplayLegend = true,
  cells,
  changeRowDataTypeFn,
  children,
  className: customClassName,
  cloneColumn,
  collapsibleColumns,
  columns,
  customDeleteConfirmation,
  data,
  deleteColumn,
  deleteMultipleRowsFn,
  deleteRowFn,
  disabled,
  disableDeleteBtnOnFirstColumn = false,
  disableVerticalScroll,
  disablePerfectScrollbar,
  displayColumnIndicator = true,
  displayDeleteRowBtn = false,
  displayRowIndicator = true,
  editorForTitles = undefined,
  fieldAttributes,
  format,
  formatDispatch,
  id,
  ignoreCurrencyChangeNumber,
  initialCurrencyType,
  isLedgerTable = false,
  isReadOnly = false,
  isToolbarVisible,
  linkCurrencyChips,
  matchCurrencyPadding,
  notShowingQuarters = false,
  onChange,
  onTitleCellsChanges,
  page,
  referencedValues,
  reverseParser,
  reverseParserOutputAccessor,
  rowConfig,
  rowGroups,
  selectionSummaryUsesCellCurrencyFormat = false,
  setCollapsibleColumns,
  setColumns,
  setIsDisplayingRowNumbers,
  setIsDisplayingToolbar = undefined,
  setRowGroups,
  setSpreadsheetWidth = undefined,
  setTitleCells,
  sheet,
  showPreviousColsDivider,
  showTitlesColumn = true,
  showToolbar,
  showTotalColumn,
  sortColumnFn = undefined,
  sortedColumn = undefined,
  tableData,
  tableTerms,
  titleData,
  toggleRows,
  totalData,
  totalsCells,
  updateCells,
  updateTotalsCells,
  validatedTitleCells,
  virtualization,
  workbook,
  disableUnsavedChanges = false,
  columnWidths,
  onColumnResize,
  columnTypes,
  onColumnReorder,
  onAddColumn,
  xlsxPreviewOptions,
}) => {
  const { isExportMode } = useLayoutContext();
  const [grid, setGrid] = useState<any[] | null>();
  const [totalGrid, setTotalGrid] = useState<any[] | null>();
  const [titleGrid, setTitleGrid] = useState<any[] | null>();
  const shouldUseXlsxPreviewLayout = Boolean(xlsxPreviewOptions);
  const shouldUseXlsxPreviewProxyCells = shouldUseXlsxPreviewLayout;
  const xlsxAllowRowLegendSelection = xlsxPreviewOptions?.allowRowLegendSelection;
  const xlsxAllowMergedSelectionIndicatorSpan = xlsxPreviewOptions?.allowMergedSelectionIndicatorSpan ?? false;
  const xlsxDefaultColumnWidths = xlsxPreviewOptions?.defaultColumnWidths;
  const xlsxDisableLegendSelection = xlsxPreviewOptions?.disableLegendSelection;
  const xlsxEnableCopyFeedback = xlsxPreviewOptions?.enableCopyFeedback;
  const xlsxFrozenPaneColumnCellKey = xlsxPreviewOptions?.frozenPaneColumnCellKey;
  const xlsxFrozenPaneRowCellKey = xlsxPreviewOptions?.frozenPaneRowCellKey;
  const xlsxOnToggleFrozenColumnsAtCell = xlsxPreviewOptions?.onToggleFrozenColumnsAtCell;
  const xlsxOnToggleFrozenRowsAtCell = xlsxPreviewOptions?.onToggleFrozenRowsAtCell;
  const xlsxReserveColumnIndicatorSpace = xlsxPreviewOptions?.reserveColumnIndicatorSpace ?? false;
  const xlsxRowHeights = xlsxPreviewOptions?.rowHeights;
  const xlsxDefaultRowHeights = xlsxPreviewOptions?.defaultRowHeights;
  const xlsxOnRowResize = xlsxPreviewOptions?.onRowResize;
  const xlsxResizeHandlesInColumnLegend = Boolean(xlsxPreviewOptions?.resizeHandlesInColumnLegend);
  const displayGrid = useMemo(() => {
    let result = grid;
    if (columnWidths || onColumnResize || xlsxRowHeights) {
      result = applyResizeToGrid(
        result,
        columnWidths ?? [],
        xlsxDefaultColumnWidths,
        columnTypes,
        onColumnResize,
        xlsxRowHeights,
        xlsxResizeHandlesInColumnLegend
      );
    }

    if (onColumnReorder) {
      result = applyColumnReorderToGrid(result, columnTypes, onColumnReorder);
    }

    if (!onAddColumn || !result) {
      return result;
    }

    const colCount = result[0]?.length ?? 0;
    const colWidth = colCount < ADD_COLUMN_LABEL_THRESHOLD ? 120 : 40;
    const label = colCount < ADD_COLUMN_LABEL_THRESHOLD ? 'Add Column +' : '+';

    return result.map((row, rowIndex) => {
      const isHeader = rowIndex === 1;
      const lastClassName = row[row.length - 1]?.className ?? '';
      return [
        ...row,
        {
          readOnly: true,
          className: isHeader ? 'add-column-header' : lastClassName,
          style: { width: colWidth, minWidth: colWidth, maxWidth: colWidth, textAlign: 'center', padding: 0 },
          value: isHeader ? (
            <Tooltip title="Add columns to this view" placement="top" arrow>
              <span
                role="button"
                tabIndex={0}
                onClick={onAddColumn}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onAddColumn();
                  }
                }}
                style={{
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: colCount < ADD_COLUMN_LABEL_THRESHOLD ? '0.85rem' : '1.2rem',
                  whiteSpace: 'nowrap',
                }}>
                {label}
              </span>
            </Tooltip>
          ) : (
            ''
          ),
        },
      ];
    });
  }, [
    grid,
    columnWidths,
    xlsxDefaultColumnWidths,
    columnTypes,
    onColumnResize,
    xlsxRowHeights,
    xlsxResizeHandlesInColumnLegend,
    onColumnReorder,
    onAddColumn,
  ]);

  const columnLabelsMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (!columnTypes) {
      return map;
    }
    const headerRow = displayGrid?.[1];
    columnTypes.forEach((colType, colIndex) => {
      const cell = headerRow?.[colIndex];
      const label = (cell && extractCellLabel(cell)) || columnTypeToLabel(colType);
      if (label) {
        map[colType] = label;
      }
    });
    return map;
  }, [displayGrid, columnTypes]);
  const [rows, setRows] = useState<any[] | undefined>();
  const [sideColumnsWidth, setSideColumnsWidth] = useState<number | undefined>();
  const [activeCell, setActiveCell] = useState<ActiveCell | undefined>();
  const [displayNumbersAndColumns, setDisplayNumbersAndColumns] = useState<boolean>(alwaysDisplayLegend);
  const [toolbarWidth, setToolbarWidth] = useState<number | undefined>();
  const { globalCurrency } = useContext(CurrencyChipContext);
  const [currency, setCurrency] = useState<string | undefined>(
    linkCurrencyChips && globalCurrency ? globalCurrency : initialCurrencyType
  );
  const { clearSelectedFlag, setClearSelected } = useDataSheetDialogActions(isLedgerTable);
  const [selectedCell, setSelectedCell] = useState<ICell | undefined>();
  const { setAction } = useContext(UnsavedChangesContext);
  const { workbookActiveCell, updateWorkbookActiveCell } = useContext(WorkbookContext);
  const [isSheetActive, setIsSheetActive] = useState<boolean>(false);
  const [isColumnDragging, setIsColumnDragging] = useState<boolean>(false);
  const columnIndicatorSpaceStyle = useMemo<React.CSSProperties | undefined>(
    () =>
      shouldUseXlsxPreviewLayout && xlsxReserveColumnIndicatorSpace
        ? { boxSizing: 'border-box', paddingTop: COLUMN_INDICATOR_HEIGHT }
        : undefined,
    [shouldUseXlsxPreviewLayout, xlsxReserveColumnIndicatorSpace]
  );

  const enableUnsavedChanges = useCallback(() => {
    if (!disableUnsavedChanges) {
      setAction({
        wrapper: ConfirmationDialog,
        content: ConfirmUnsavedChanges,
      });
    }
  }, [setAction, disableUnsavedChanges]);

  const checkChanges = useCallback(
    (changes: CellChange[]) => {
      // check if the cell has changed compared to the original value
      if (changes.length > 0) {
        enableUnsavedChanges();
      } else {
        setAction(false);
      }
    },
    [enableUnsavedChanges, setAction]
  );

  const className = customClassName ?? sheet.name;

  const dialogContext = useContext(DialogContext);

  const tableRef = useRef<HTMLDivElement>(null);
  const titlesColumnRef = useRef<HTMLDivElement>(null);
  const totalsColumnRef = useRef<HTMLDivElement>(null);
  const contentColumnRef = useRef<HTMLDivElement>(null);

  // Last successfully computed spreadsheet width
  const spreadsheetWidthFallback = useRef<number>(0);

  const handleDeleteColumn = useCallback(
    (columnIndex: number, columnId: string | number, wb: any, cb: () => void) => {
      if (allowConfirmAndDeleteColumn && !customDeleteConfirmation) {
        const { showDialog } = dialogContext;
        const ContentComponent = <ConfirmationDelete itemName={tableTerms?.columnName ?? ''} />;

        showDialog(
          getCancelDialogOptions(
            () => ContentComponent,
            () => deleteColumn && deleteColumn(columnIndex, columnId, wb, cb)
          )
        );
      } else {
        deleteColumn && deleteColumn(columnIndex, columnId, wb, cb);
      }
      enableUnsavedChanges();
    },
    [
      allowConfirmAndDeleteColumn,
      customDeleteConfirmation,
      deleteColumn,
      dialogContext,
      enableUnsavedChanges,
      tableTerms?.columnName,
    ]
  );

  const displayLegend = useMemo(
    () => displayNumbersAndColumns || (showToolbar && !isEmpty(activeCell)),
    [activeCell, displayNumbersAndColumns, showToolbar]
  );

  const shouldSyncLegendVisibilityFromOptions =
    shouldUseXlsxPreviewLayout &&
    (xlsxDisableLegendSelection ||
      xlsxReserveColumnIndicatorSpace ||
      Boolean(xlsxRowHeights?.length || xlsxDefaultRowHeights?.length));

  useEffect(() => {
    if (shouldSyncLegendVisibilityFromOptions) {
      setDisplayNumbersAndColumns(alwaysDisplayLegend);
    }
    if (setIsDisplayingRowNumbers) {
      setIsDisplayingRowNumbers(alwaysDisplayLegend);
    }
  }, [alwaysDisplayLegend, setIsDisplayingRowNumbers, shouldSyncLegendVisibilityFromOptions]);

  const generateLegendRow = useCallback(() => {
    const legendRow: any[] = [];
    const { alphabet } = sheet;
    const totalColumns = sheet.columns.length;
    const sortedColumns = sortBy(columns, ['order']);
    const columnWidthMap = new Map(columnWidths?.map(({ column, width }) => [column, width]) ?? []);
    if (columns) {
      for (let colIndex = 0; colIndex < totalColumns; colIndex += 1) {
        const colLegend = alphabet?.[colIndex] ?? '';
        const column = sortedColumns[colIndex];
        const columnWidth = columnWidthMap.get(columnTypes?.[colIndex] ?? colLegend);
        const columnId = column.id ?? 0;
        const columnRef = cells?.[colLegend + 1] ? cells[colLegend + 1].columnRef : '';

        // todo: this kind of logic should be moved elsewhere
        const isVisibleColumn = column.isAdditionalHistoricalYear
          ? column.isAdditionalHistoricalYear && column.showAdditionalHistoricalYear
          : null;
        const sourceLegendStyle = sheet.cells?.[`${colLegend}1`]?.style;
        const legendStyle =
          shouldUseXlsxPreviewLayout || columnWidth != null
            ? getLegendCellLayoutStyle(sourceLegendStyle, columnWidth)
            : (sourceLegendStyle ?? {});
        const legendCell = {
          readOnly: true,
          className: 'legend col-legend',
          value: colLegend,
          columnId,
          colLegend,
          columnRef,
          disableEvents: true,
          isChildWithDivider: column.isChildWithDivider,
          isParentWithDivider: column.isParentWithDivider,
          isParent: column.isParent,
          childIndex: column.quarter,
          parentColumn: column.parentColumn,
          isVisibleColumn,
          isLegend: true,
          needsSpacer: column.needsSpacer,
          style: legendStyle,
        };

        legendRow.push(legendCell);
      }
      return legendRow;
    }
    return [];
  }, [cells, columns, columnTypes, columnWidths, sheet, shouldUseXlsxPreviewLayout]);

  const legendRow = useMemo(() => generateLegendRow(), [generateLegendRow]);

  const getClassnamesForLegends = useCallback(
    (classesString: string) => (isToolbarVisible || !displayLegend ? `${classesString} hidden-legend` : classesString),
    [displayLegend, isToolbarVisible]
  );

  const rowLegendCell = useMemo(
    () => ({
      value: '',
      readOnly: true,
      className: 'legend row-legend col-legend cell read-only',
    }),
    []
  );

  const defaultLegendCell = useMemo(
    () => ({
      ...rowLegendCell,
      className: getClassnamesForLegends('legend col-legend'),
    }),
    [rowLegendCell, getClassnamesForLegends]
  );

  const titleRowSpacerCell = useMemo(
    () => ({
      value: '',
      readOnly: true,
      className: 'row-label cell read-only visible',
    }),
    []
  );

  const createRowLegendPlaceholder = useCallback((sourceRow: any[], rowIndex: number) => {
    const isXlsxPaddingRow = sourceRow.some((cell: any) => cell?.isXlsxPaddingRow);
    const sourceLegend = sourceRow.find((cell: any) => cell?.isLegend || typeof cell?.rowNumber === 'number');
    const sourceGridCell = sourceRow.find((cell: any) => typeof cell?.x === 'number');
    const rowNumber = isXlsxPaddingRow ? undefined : (sourceLegend?.rowNumber ?? rowIndex + 1);

    return {
      value: rowNumber && rowNumber > 0 ? rowNumber : '',
      readOnly: true,
      className: 'legend row-legend cell read-only visible',
      isLegend: true,
      rowNumber,
      x: sourceLegend?.x ?? sourceGridCell?.x ?? rowIndex,
      style: sourceLegend?.style,
    };
  }, []);

  const headerLegendRow = useMemo(() => {
    if (displayNumbersAndColumns) {
      const headerRow = [rowLegendCell];
      if (showTitlesColumn) {
        headerRow.push(defaultLegendCell);
      }
      return headerRow;
    }
    return [defaultLegendCell];
  }, [showTitlesColumn, displayNumbersAndColumns, defaultLegendCell, rowLegendCell]);

  const isVisibleGridRow = useCallback(
    (rowData: any[]) =>
      rowData.filter(({ isVisible }: { isVisible?: boolean }) => isVisible === true || isUndefined(isVisible)).length >
      0,
    []
  );

  const filterData = useCallback((gridData: any[]) => gridData.filter(isVisibleGridRow), [isVisibleGridRow]);

  const generateGrid = useCallback(
    (titleData: any[], tmpData: any[], totalData: any[]) => {
      const gridData = filterData(tmpData);
      const shouldBackfillTitleRows =
        shouldUseXlsxPreviewLayout &&
        (xlsxReserveColumnIndicatorSpace || Boolean(xlsxRowHeights?.length || xlsxDefaultRowHeights?.length));
      const titleGridData = shouldBackfillTitleRows
        ? tmpData
          .map((sourceRow, sourceRowIndex) => ({ sourceRow, sourceRowIndex }))
          .filter(({ sourceRow }) => isVisibleGridRow(sourceRow))
          .map(({ sourceRow, sourceRowIndex }) => {
            const matchingTitleRow = titleData[sourceRowIndex];
            const titlesCell = matchingTitleRow?.length
              ? matchingTitleRow
              : [createRowLegendPlaceholder(sourceRow, sourceRowIndex)];
            const legendCell = titlesCell[0] ?? createRowLegendPlaceholder(sourceRow, sourceRowIndex);
            const titleCell = titlesCell[1];
            const gridCells: any[] = [];
            if (titleCell && showTitlesColumn) {
              gridCells.push(titleCell);
            }
            if (legendCell && displayNumbersAndColumns) {
              gridCells.unshift(legendCell);
            }
            if (showTitlesColumn && gridCells.length === 0) {
              gridCells.push(titleRowSpacerCell);
            }
            return gridCells;
          })
        : filterData(titleData).map(titlesCell => {
          const legendCell = titlesCell[0];
          const titleCell = titlesCell[1];
          const gridCells: any[] = [];
          if (titleCell && showTitlesColumn) {
            gridCells.push(titleCell);
          }
          if (legendCell && displayNumbersAndColumns) {
            gridCells.unshift(legendCell);
          }
          return gridCells;
        });
      const totalGridData = filterData(totalData);

      if (!isToolbarVisible) {
        titleGridData.unshift(headerLegendRow);
        gridData.unshift(legendRow);
        totalGridData.unshift([defaultLegendCell]);
      }

      setTitleGrid(titleGridData);
      setGrid(gridData);
      setTotalGrid(totalGridData);
    },
    [
      showTitlesColumn,
      isToolbarVisible,
      displayNumbersAndColumns,
      legendRow,
      headerLegendRow,
      defaultLegendCell,
      titleRowSpacerCell,
      createRowLegendPlaceholder,
      filterData,
      isVisibleGridRow,
      xlsxRowHeights,
      xlsxDefaultRowHeights,
      xlsxReserveColumnIndicatorSpace,
      shouldUseXlsxPreviewLayout,
    ]
  );

  useEffect(() => {
    if (sheet.data && sheet.totalData) {
      generateGrid(sheet.titleData || [], sheet.data, sheet.totalData);
    } else {
      setGrid(null);
      setTotalGrid(null);
      setTitleGrid(null);
    }
  }, [cells, data, titleData, totalData, collapsibleColumns, rowGroups, disabled, sheet, generateGrid]);

  useEffect(() => {
    if (linkCurrencyChips && globalCurrency) {
      setCurrency(globalCurrency);
    }
  }, [globalCurrency, linkCurrencyChips]);

  useEffect(() => {
    const contentColumns = contentColumnRef?.current;

    const contentColumnsWidth = getNumberValue(contentColumns?.offsetWidth);
    const spreadsheetWidth = getNumberValue(sideColumnsWidth) + contentColumnsWidth;

    if (contentColumnsWidth > 0) {
      spreadsheetWidthFallback.current = spreadsheetWidth;
    }

    setToolbarWidth(spreadsheetWidthFallback.current);
    setSpreadsheetWidth?.(spreadsheetWidthFallback.current);
  }, [sideColumnsWidth, collapsibleColumns, setSpreadsheetWidth]);

  useEffect(() => {
    if (isArray(rowConfig)) {
      const visibleRows = rowConfig.filter(({ isVisible }) => isVisible !== false);
      setRows(visibleRows);
    }
  }, [rowConfig]);

  useEffect(() => {
    setIsDisplayingToolbar?.(Boolean(showToolbar && !isEmpty(activeCell)));
  }, [activeCell, setIsDisplayingToolbar, showToolbar]);

  useLayoutEffect(() => {
    let getSideColumnsTimer: NodeJS.Timeout;

    if (titleGrid && totalGrid) {
      getSideColumnsTimer = setTimeout(() => {
        const titlesWidth = getNumberValue(titlesColumnRef.current?.offsetWidth);
        const totalsWidth = getNumberValue(totalsColumnRef.current?.offsetWidth);

        setSideColumnsWidth(titlesWidth + totalsWidth);
      }, GET_SIDE_COLUMNS_DELAY);
    }

    return () => clearTimeout(getSideColumnsTimer);
  }, [titleGrid, totalGrid]);

  const computeCellsChanges = useCallback(
    (changes: CellChange[]) => {
      // XLSX previews render proxy cells for mapping/citations; normal spreadsheets already edit workbook cells directly.
      const resolvedChanges = shouldUseXlsxPreviewProxyCells
        ? changes.map(change => ({
          ...change,
          cell: resolveXlsxPreviewChangeCell(change.cell),
        }))
        : changes;

      checkChanges(resolvedChanges.filter(change => !change.cell.ignoreExpr));
      resolvedChanges.forEach(({ cell, value }, index) => {
        onChange(cell, value);
        if (shouldUseXlsxPreviewProxyCells) {
          syncXlsxPreviewProxyCell(changes[index].cell, cell, value);
        }
      });
      if (toggleRows) {
        toggleRows();
      }
    },
    [checkChanges, onChange, shouldUseXlsxPreviewProxyCells, toggleRows]
  );

  const decoratedReverseParser = useCallback(
    (cells: any, columns: any) => {
      if (reverseParser) {
        const output = (
          reverseParser as (arg: {
            cells: ScalarSpreadsheetProps['cells'];
            columns: ScalarSpreadsheetProps['columns'];
            fieldAttributes: ScalarSpreadsheetProps['fieldAttributes'];
            tableData: ScalarSpreadsheetProps['tableData'];
          }) => unknown
        )({ cells, columns, fieldAttributes, tableData });

        // Some reverse parsers return a payload object; the accessor selects the persisted column data.
        if (reverseParserOutputAccessor && output) {
          return getValueByAccessorString(output, reverseParserOutputAccessor);
        }
        return output;
      }
      return undefined;
    },
    [reverseParser, fieldAttributes, reverseParserOutputAccessor, tableData]
  );

  // update the workbooks active cell if we can. The workbook will highlight the referenced cells.
  useEffect(() => {
    if (updateWorkbookActiveCell) {
      updateWorkbookActiveCell(activeCell);
    }
  }, [activeCell, updateWorkbookActiveCell]);

  // inactive sheets won't display the cell reference bar. Should prevent confusion about which cells are actively being referenced
  useEffect(() => {
    const workbookActiveCellSheetName = workbookActiveCell?.cellInfo?.sheet?.name ?? workbookActiveCell?.sheet?.name;
    const activeCellSheetName = activeCell?.sheet?.name;
    if (!(activeCellSheetName && workbookActiveCellSheetName)) {
      setIsSheetActive(false);
    } else if (activeCellSheetName !== workbookActiveCellSheetName) {
      setIsSheetActive(false);
    } else {
      setIsSheetActive(true);
    }
  }, [workbookActiveCell, activeCell]);

  const stripeRowParityMap = useMemo(() => {
    const stripeRowConfig: StripeRowConfig =
      sheet && 'stripeRowConfig' in sheet
        ? (sheet as { stripeRowConfig: StripeRowConfig }).stripeRowConfig
        : { hasColTitle: (sheet as { hasColTitle?: boolean } | undefined)?.hasColTitle };

    return buildStripeRowParityMap(rowConfig ?? [], rowGroups, stripeRowConfig);
  }, [rowConfig, rowGroups, sheet]);

  const contextValue = useMemo(
    () => ({
      rows,
      columns,
      cells,
      totalsCells,
      updateCells,
      updateTotalsCells,
      tableTerms,
      tableData,
      rowGroups,
      setRowGroups,
      titlesColumnRef,
      totalsColumnRef,
      onCellsChanged: computeCellsChanges,
      setColumns,
      allowReorderColumns,
      disabled,
      activeCell,
      setActiveCell,
      displayNumbersAndColumns,
      setDisplayNumbersAndColumns,
      deleteMultipleRowsFn,
      deleteRowFn,
      setCollapsibleColumns,
      collapsibleColumns,
      currency,
      setCurrency,
      ignoreCurrencyChangeNumber,
      showPreviousColsDivider,
      onTitleCellsChanges,
      setTitleCells,
      validatedTitleCells,
      workbook,
      page,
      format,
      formatDispatch,
      getClassnamesForLegends,
      clearSelectedFlag,
      setClearSelected,
      displayLegend,
      isToolbarVisible,
      displayDeleteRowBtn,
      reverseParser: decoratedReverseParser,
      notShowingQuarters,
      selectedCell,
      setSelectedCell,
      allowConfirmAndDeleteColumn,
      allowDeleteOnlySpecificColumn,
      displayRowIndicator,
      displayColumnIndicator,
      allowCopyColumn,
      allowDeleteColumn,
      handleDeleteColumn,
      disableDeleteBtnOnFirstColumn,
      allowCopyRows,
      allowCloneColumn,
      cloneColumn,
      allowAddMultipleRows,
      addMultipleRows,
      allowAddMultipleColumns,
      addMultipleColumns,
      allowAddSingleRow,
      addSingleRow,
      showToolbar,
      allowDeleteRow,
      allowMergedSelectionIndicatorSpan: xlsxAllowMergedSelectionIndicatorSpan,
      isReadOnly,
      allowChangeDataType,
      changeRowDataTypeFn,
      allowSortColumn,
      sortColumnFn,
      sortedColumn,
      isColumnDragging,
      stripeRowParityMap,
    }),
    [
      activeCell,
      allowReorderColumns,
      cells,
      clearSelectedFlag,
      collapsibleColumns,
      columns,
      computeCellsChanges,
      currency,
      decoratedReverseParser,
      deleteMultipleRowsFn,
      deleteRowFn,
      disabled,
      displayDeleteRowBtn,
      displayLegend,
      format,
      formatDispatch,
      getClassnamesForLegends,
      ignoreCurrencyChangeNumber,
      isToolbarVisible,
      onTitleCellsChanges,
      page,
      rowGroups,
      rows,
      setClearSelected,
      setCollapsibleColumns,
      setColumns,
      setRowGroups,
      setTitleCells,
      showPreviousColsDivider,
      tableData,
      tableTerms,
      totalsCells,
      updateCells,
      updateTotalsCells,
      validatedTitleCells,
      workbook,
      displayNumbersAndColumns,
      notShowingQuarters,
      selectedCell,
      setSelectedCell,
      allowConfirmAndDeleteColumn,
      allowDeleteOnlySpecificColumn,
      displayRowIndicator,
      displayColumnIndicator,
      allowCopyColumn,
      allowDeleteColumn,
      handleDeleteColumn,
      disableDeleteBtnOnFirstColumn,
      allowCopyRows,
      allowCloneColumn,
      cloneColumn,
      allowAddMultipleRows,
      addMultipleRows,
      allowAddMultipleColumns,
      addMultipleColumns,
      allowAddSingleRow,
      addSingleRow,
      showToolbar,
      allowDeleteRow,
      xlsxAllowMergedSelectionIndicatorSpan,
      isReadOnly,
      allowChangeDataType,
      changeRowDataTypeFn,
      allowSortColumn,
      sortColumnFn,
      sortedColumn,
      isColumnDragging,
      stripeRowParityMap,
    ]
  );

  if (isUndefined(columns) || (!isEmpty(columns) && !grid && !totalGrid)) {
    return <GridSkeleton />;
  }

  return (
    <Root
      data-testid={tableTerms?.tableSlug}
      className={`${tableTerms?.tableSlug}-sc-spreadsheet ${classes.relative} spreadsheet-container${isExportMode ? ' export-mode' : ''}`}
      id={id}>
      {children}
      {!isEmpty(columns) &&
        !isEmpty(data) &&
        !isEmpty(displayGrid) &&
        ((showTotalColumn && !isEmpty(totalGrid)) || !showTotalColumn) && (
        <FeaturedSpreadsheetContext.Provider value={contextValue}>
          <DragReorderContext
            enabled={!!onColumnReorder}
            columnTypes={columnTypes}
            onColumnReorder={onColumnReorder}
            columnLabels={columnLabelsMap}
            onDragStart={() => setIsColumnDragging(true)}
            onDragEnd={() => setIsColumnDragging(false)}>
            <Box id={`spreadsheet-table__${tableTerms?.tableSlug}`} ref={tableRef} display="flex">
              <div
                ref={titlesColumnRef}
                id="spreadsheet-table__title-columns"
                className="titles-columns-container"
                style={columnIndicatorSpaceStyle}>
                <RowIndicator
                  frozenPaneCellKey={shouldUseXlsxPreviewLayout ? xlsxFrozenPaneRowCellKey : undefined}
                  onToggleFrozenPaneAtCell={shouldUseXlsxPreviewLayout ? xlsxOnToggleFrozenRowsAtCell : undefined}
                  tableRef={tableRef}
                />
                <TitlesColumn
                  allowRowLegendSelection={xlsxAllowRowLegendSelection}
                  data={titleGrid}
                  className={className}
                  currency={format?.currency}
                  dataEditor={editorForTitles}
                  onRowResize={xlsxOnRowResize}
                  defaultRowHeights={xlsxDefaultRowHeights}
                  rowHeights={xlsxRowHeights}
                  tableSlug={tableTerms?.tableSlug}
                  disableLegendSelection={xlsxDisableLegendSelection}
                  enableXlsxPreviewLayout={shouldUseXlsxPreviewLayout}
                />
              </div>
              <div
                className={classes.relative}
                style={{
                  maxWidth: `calc(100% - ${sideColumnsWidth}px)`,
                }}
                id="spreadsheet-table__content-columns"
                ref={contentColumnRef}>
                <ContentColumns
                  data={displayGrid}
                  className={className}
                  tableSlug={tableTerms?.tableSlug}
                  isLedgerTable={isLedgerTable}
                  disableVerticalScroll={disableVerticalScroll}
                  disablePerfectScrollbar={disablePerfectScrollbar}
                  disableLegendSelection={xlsxDisableLegendSelection}
                  enableCopyFeedback={xlsxEnableCopyFeedback}
                  frozenPaneColumnCellKey={shouldUseXlsxPreviewLayout ? xlsxFrozenPaneColumnCellKey : undefined}
                  onToggleFrozenColumnsAtCell={
                    shouldUseXlsxPreviewLayout ? xlsxOnToggleFrozenColumnsAtCell : undefined
                  }
                  reserveColumnIndicatorSpace={xlsxReserveColumnIndicatorSpace}
                  virtualization={virtualization}
                />
              </div>
              <div
                ref={totalsColumnRef}
                id="spreadsheet-table__total-column"
                className="total-columns-container"
                style={columnIndicatorSpaceStyle}>
                {showTotalColumn && (
                  <TotalsColumn data={totalGrid} className={className} currency={format?.currency} />
                )}
              </div>
            </Box>
          </DragReorderContext>
          <div
            className={classes.bottomBar}
            style={{ width: toolbarWidth ? toolbarWidth - ROW_INDICATOR_INSET : undefined }}>
            {showToolbar && isSheetActive && !isEmpty(activeCell) && (
              <div className={classes.cellReferenceWrapper}>
                <CellReferenceBar
                  id={tableTerms?.tableSlug}
                  cell={activeCell}
                  referencedValues={referencedValues}
                  matchCurrencyPadding={matchCurrencyPadding}
                />
              </div>
            )}
            <SelectionSummaryBar
              selectedCell={selectedCell}
              data={displayGrid}
              useCellCurrencyFormat={selectionSummaryUsesCellCurrencyFormat}
            />
          </div>
        </FeaturedSpreadsheetContext.Provider>
      )}
    </Root>
  );
};

// Default props
ScalarSpreadsheet.defaultProps = {
  allowCloneColumn: false,
  allowConfirmAndDeleteColumn: true,
  allowDeleteOnlySpecificColumn: false,
  className: '',
  colTitleRow: 0,
  customDeleteConfirmation: false,
  disabled: false,
  disableDeleteBtnOnFirstColumn: false,
  hasColTitle: true,
  ignoreCurrencyChangeNumber: 0,
  initialCurrencyType: `${DEFAULT_CURRENCY} ${DEFAULT_CURRENCY_SYMBOL}`,
  isToolbarVisible: false,
  referencedValues: {},
  rowConfig: [],
  showPreviousColsDivider: false,
  showTotalColumn: true,
  tableTerms: {
    tableName: 'Table',
    columnName: 'Column',
    pluralColumnName: 'Columns',
  },
};

export default ScalarSpreadsheet;
